import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, TreeSelect, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import './styleEditDeviceGroup.css';
import { useGroupsStore } from '../../../store/groups/Groups';
import { Group } from '../../../types/Group';
import { useAuthStore } from '../../../store/auth/auth';
import { getGroupEditDevice } from '../../../api/devices/getEditDeviceGroup';
import { Key } from 'antd/lib/table/interface';
import { useButtonsFromAllcams } from '../../../store/devices/useButtonsFromAllcams';
import { useSelectedRowKeys } from '../../../store/devices/useSelectedRowKeys';
import { DeviceByGroupStore } from '../../../store/devices/DeviceByGroupStore';
import { Device } from '../../../types/Device';


interface NewGroupModalProps {
    visible: boolean;
    onOk: () => void;
    onCancel: () => void;
    device: Key[];
}
const EditDeviceGroup: React.FC<NewGroupModalProps> = ({ visible, onOk, onCancel, device }) => {
    const { groups, fetchGroups } = useGroupsStore();
    const { user, SmartDVRToken } = useAuthStore();
    const [groupUID, setGroupUID] = useState<string | undefined>(undefined);
    const { setIsEditDeviceGroupModal } = useButtonsFromAllcams();
    const { allDevices } = DeviceByGroupStore();
    const { setSelectedRowKeys } = useSelectedRowKeys();
    const [form] = Form.useForm();
    const [formInitialized, setFormInitialized] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        if (visible && groups.length === 0) {
            fetchGroups();
        }
    }, [visible, groups.length]);

    useEffect(() => {
        if (visible) {
            setFormInitialized(true);
        } else {
            setFormInitialized(false);
        }
    }, [visible]);

    useEffect(() => {
        if (visible && device.length > 0 && allDevices.length > 0) {
            const selectedDevices = allDevices.filter(d => device.includes(d.UID));

            if (selectedDevices.length === 1) {
                const groupUID = selectedDevices[0].groupUID;
                if (groupUID === '00000000-0000-0000-0000-000000000002') {
                    setGroupUID(undefined);
                    if (form && formInitialized) {
                        form.setFieldsValue({ parent_uid: undefined });
                    }
                } else {
                    setGroupUID(groupUID);
                    if (form && formInitialized) {
                        form.setFieldsValue({ parent_uid: groupUID });
                    }
                }
            } else if (selectedDevices.length > 1) {
                const firstGroupUID = selectedDevices[0].groupUID;
                const allSameGroup = selectedDevices.every(d => d.groupUID === firstGroupUID);
                if (allSameGroup && firstGroupUID !== '00000000-0000-0000-0000-000000000002') {
                    setGroupUID(firstGroupUID);
                    if (form && formInitialized) {
                        form.setFieldsValue({ parent_uid: firstGroupUID });
                    }
                } else {
                    setGroupUID(undefined);
                    if (form && formInitialized) {
                        form.setFieldsValue({ parent_uid: undefined });
                    }
                }
            }
        } else {
            setGroupUID(undefined);
            if (form && formInitialized) {
                form.setFieldsValue({ parent_uid: undefined });
            }
        }
    }, [visible, device, allDevices, form, formInitialized]);

    const handleParentUidChange = (value: string) => {
        setGroupUID(value);
    };

    const handleCancel = () => {
        if (form && formInitialized) {
            form.resetFields();
        }
        setGroupUID(undefined);
        onCancel();
    };

    const getAllSubGroups = useCallback((group: Group): Group[] => {
        if (!group?.sub_groups) return [];
        let subGroups: Group[] = [];
        group.sub_groups.forEach((subGroup) => {
            subGroups.push(subGroup);
            if (subGroup.sub_groups?.length > 0) {
                subGroups = [...subGroups, ...getAllSubGroups(subGroup)];
            }
        });
        return subGroups;
    }, []);

    const convertGroupsToTreeData = useCallback((groups: Group[]): any[] => {
        return groups
            .filter((group) =>
                group.uid !== '00000000-0000-0000-0000-000000000002' &&
                group.uid !== '00000000-0000-0000-0000-000000000003' &&
                group.name !== 'Несортированные'
            )
            .map((group) => ({
                title: group.name,
                value: group.uid,
                key: group.uid,
                children: group.sub_groups ? convertGroupsToTreeData(group.sub_groups) : undefined,
            }));
    }, []);

    const treeData = useMemo(() => {
        const rootGroup = groups.find(group => group.name === 'Root');
        if (rootGroup && rootGroup.sub_groups) {
            return convertGroupsToTreeData(rootGroup.sub_groups);
        }
        return convertGroupsToTreeData(groups);
    }, [groups, convertGroupsToTreeData]);

    const handleOk = async () => {
        if (!user) {
            message.error('Ошибка авторизации!');
            return;
        }

        if (!form || !formInitialized) {
            messageApi.error('Ошибка формы!');
            return;
        }

        const values = await form.validateFields();
        const groupUIDToUse = values.parent_uid || '00000000-0000-0000-0000-000000000002';
        const groupData = { group_uid: groupUIDToUse, uid: device };

        try {
            const response = await getGroupEditDevice(SmartDVRToken, user.login, groupData as any);

            if (response.success) {
                let groupName = 'Несортированные';
                if (values.parent_uid) {
                    const allGroups = [...groups];
                    groups.forEach(group => {
                        if (group.sub_groups) {
                            allGroups.push(...getAllSubGroups(group));
                        }
                    });
                    const foundGroup = allGroups.find(g => g.uid === values.parent_uid);
                    groupName = foundGroup?.name || 'выбранную группу';
                }
                messageApi.success(`Устройства успешно перемещены в группу "${groupName}"!`);
                if (form && formInitialized) {
                    form.resetFields();
                }
                setGroupUID(undefined);
                setSelectedRowKeys([]);
                setIsEditDeviceGroupModal(false);
                onOk();
            } else {
                messageApi.error(`Ошибка: ${response.error}`);
            }
        } catch (error) {
            messageApi.error('Ошибка при обновлении группы устройств!');
            console.error('Error updating group:', error);
        }
    };

    return (
        <>
            {contextHolder}
            <Modal
                title={
                    <div className="titleModal_Group">
                        <span className="spanGroup">Редактировать устройства</span>
                        <CloseOutlined onClick={onCancel} className="closeBut_GroupEdit" />
                    </div>
                }
                className="newGroup_modal"
                visible={visible}
                onOk={onOk}
                onCancel={handleCancel}
                width={720}
                closable={false}
                footer={
                    <Button className="buttonAdd" onClick={handleOk}>
                        Сохранить
                    </Button>
                }
            >
                <Form form={form}>
                    <Form.Item
                        className="form_Group"
                        label={<span className="inputLabel_Group">Группа</span>}
                        name="parent_uid"
                        required={false}
                    >
                        <TreeSelect
                            className="selectGroup"
                            placeholder="Введите название или выберите из списка"
                            onChange={handleParentUidChange}
                            treeData={treeData}
                            showSearch
                            allowClear
                            treeDefaultExpandAll
                            filterTreeNode={(inputValue, treeNode) => {
                                const title = String(treeNode.title || '');
                                return title.toLowerCase().includes(inputValue.toLowerCase());
                            }}
                            treeNodeFilterProp="title"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default EditDeviceGroup;
